import { useEffect, useState } from "react";
import { NAV_LINKS, BRAND } from "@/data/content";
import { Menu, X, Phone } from "lucide-react";

export default function Nav({ onBook }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-ng-bg/85 border-b border-ng-line"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-4">
        <a
          href="#top"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <span className="font-display italic font-black text-2xl md:text-3xl tracking-tight leading-none flex">
            <span className="chrome-text">{BRAND.short_split.silver}</span>
            <span className="electric-text">{BRAND.short_split.blue}</span>
          </span>
          <span className="hidden sm:inline-block font-display text-[10px] tracking-[0.35em] text-ng-blue uppercase border-l border-ng-line pl-3">
            Performance
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8 text-sm font-medium">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="nav-link text-white/80 uppercase tracking-[0.22em] text-xs font-display"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={`tel:${BRAND.phone_link}`}
            data-testid="nav-phone"
            className="hidden md:inline-flex items-center gap-2 text-white/70 hover:text-ng-blue transition-colors text-xs font-display tracking-widest uppercase"
          >
            <Phone size={14} strokeWidth={1.5} />
            {BRAND.phone}
          </a>
          <button
            data-testid="nav-cta-book"
            onClick={onBook}
            className="hidden md:inline-flex items-center gap-2 bg-ng-blue text-white px-5 py-2.5 uppercase tracking-[0.18em] text-xs font-bold font-display hover:bg-ng-blueLight transition-colors shadow-[0_0_24px_-4px_rgba(30,144,255,0.6)]"
          >
            Apply Now
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-white p-2"
            aria-label="Open menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="lg:hidden bg-ng-bg/95 backdrop-blur-xl border-t border-ng-line"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-white/80 uppercase tracking-[0.22em] text-sm py-2 border-b border-white/5 font-display"
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
              >
                {l.label}
              </a>
            ))}
            <button
              data-testid="nav-mobile-cta-book"
              onClick={() => {
                setOpen(false);
                onBook();
              }}
              className="bg-ng-blue text-white px-6 py-3 uppercase tracking-[0.18em] text-xs font-bold font-display mt-2"
            >
              Apply Now
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
